import App from '@app/app.js';
import './cart-reward-tracker.component.scss';
import type CartRewardTrackerService from '@app/components/cart-reward-tracker/cart-reward-tracker.service';
import { SESSION_STORAGE_KEYS } from '@app/constant/session-storage-keys';
import type { IShoppingCartStore } from '@app/interface/cart/shopping-cart-store.interface';
import type { ISubject } from '@app/interface/cart/subject.interface';
import type { IConfettiService } from '@app/interface/confetti/confetti-service.interface';
import type { IAutoAppliedCoupon } from '@app/interface/content/auto-applied-coupons.interface';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import type { IJobStore } from '@app/interface/job/job-store.interface';
import type { ProductCatalogStore } from '@app/interface/store/product-catalog-store.interface';
import type SessionStorageService from '@app/service/session-storage.service';
import template from './cart-reward-tracker.component.html';

export interface RewardUpsellProduct {
  id: string;
  nameDesc: string;
  skuCode: string;
  defaultLayoutThemeId: string;
  discountedPriceWithPackage: number;
  unitPrice: number;
  showSuccess: boolean;
  successMessageKey: string | null;
  freeGift: boolean;
}
enum State {
  NA,
  EMPTY_TO_FREE_SHIPPING,
  EMPTY_TO_FREE_GIFT,
  NONE_TO_FREE_SHIPPING,
  NONE_TO_FREE_GIFT,
  FREE_SHIPPING_END,
  FREE_SHIPPING_TO_FREE_GIFT,
  FREE_GIFT
}

const SUCCESS_DURATION: number = 30 * 1000;
const MARKER_WIDTH: string = '3rem';

export class RewardTrackerController {
  public readonly State = State;

  private cartTotalAmount: number = 0;
  public freeShippingCoupon: IAutoAppliedCoupon | undefined;
  public freeGiftCoupon: IAutoAppliedCoupon | undefined;
  public enabled: boolean;
  public state: State;
  public upsellProducts: RewardUpsellProduct[] = [];
  public currentProductIndex: number = 0;
  public showSuccess: boolean = false;
  public successMessageKey: string | null = null;
  private successTimeoutRef: ng.IPromise<void> | null = null;
  public confettiTriggered: boolean = false;
  private suppressConfetti = false;
  public freeGiftProduct: RewardUpsellProduct | null = null;

  public onAddToCart?: (locals: { product: RewardUpsellProduct }) => Promise<void>;

  constructor(
    private shoppingCartStore: IShoppingCartStore,
    private contentStore: IContentStore,
    private jobStore: IJobStore,
    private cartRewardTrackerService: CartRewardTrackerService,
    private confettiService: IConfettiService,
    private $scope: ng.IScope,
    private sessionStorageService: SessionStorageService,
    private productCatalogStore: ProductCatalogStore,
    private $timeout: ng.ITimeoutService,
    private $rootScope: ng.IRootScopeService
  ) {
    this.freeShippingCoupon = this.cartRewardTrackerService.getFreeShippingCoupon();
    this.freeGiftCoupon = this.cartRewardTrackerService.getFreeGiftCoupon();
    this.enabled = Boolean(this.isFreeGiftAvailable) || Boolean(this.isFreeShippingAvailable);
    this.state = this.resolveState();
    this.$scope.$watch(
      () => this.shoppingCartStore.getSubtotalAfterDiscounts(),
      (subtotalAfterDiscounts: number) => {
        this.cartTotalAmount = Math.max(0, parseInt(subtotalAfterDiscounts.toString(), 10));
        this.enabled = Boolean(this.isFreeGiftAvailable) || Boolean(this.isFreeShippingAvailable);
        this.state = this.resolveState();
        if (!this.suppressConfetti) {
          this.triggerConfetti();
        }
        this.updateUpsellProducts();
      }
    );

    const freeGiftAddedUnsubscribe = this.$rootScope.$on(
      'shoppingCart:freeGiftAdded',
      (_: any, freeGiftProduct: RewardUpsellProduct) => {
        this.showSuccessForFreeGift(freeGiftProduct);
        this.updateUpsellProducts();
      }
    );

    const freeGiftRemovedUnsubscribe = this.$rootScope.$on('shoppingCart:freeGiftRemoved', () => {
      this.updateUpsellProducts();
    });

    this.$scope.$on('$destroy', () => {
      freeGiftAddedUnsubscribe();
      freeGiftRemovedUnsubscribe();
    });
  }

  private resetFreeGiftState(): void {
    this.freeGiftProduct = null;
    if (this.successTimeoutRef) {
      this.$timeout.cancel(this.successTimeoutRef);
    }
  }

  get cartTotal(): number {
    return this.cartTotalAmount;
  }

  get isFreeGiftAvailable(): boolean {
    return Boolean(
      this.freeGiftCoupon && this.productCatalogStore.findAddOnBySkuFromAllJobs(this.freeGiftCoupon.giftSku)
    );
  }

  private triggerConfetti(): void {
    if (this.confettiTriggered) {
      return;
    }
    if (this.sessionStorageService.get(SESSION_STORAGE_KEYS.LT_CONFETTI_FIRED)) {
      return;
    }
    const isFinishState = this.state === State.FREE_GIFT || this.state === State.FREE_SHIPPING_END;
    if (!this.enabled || !isFinishState) {
      return;
    }
    this.confettiService.fire();
    this.confettiTriggered = true;
    this.sessionStorageService.set(SESSION_STORAGE_KEYS.LT_CONFETTI_FIRED, true);
  }

  get isFreeShippingAvailable(): boolean {
    return (
      Boolean(this.freeShippingCoupon) &&
      !this.shoppingCartStore.hasShipOrdersToSubject() &&
      this.shoppingCartStore.hasShipToHome()
    );
  }

  get freeShippingAchieved(): boolean {
    if (!this.freeShippingCoupon) {
      return false;
    }
    const freeShippingOrderMinCost = Number(this.freeShippingCoupon.orderMinCost) || 0;
    return this.isFreeShippingAvailable && this.cartTotal >= freeShippingOrderMinCost;
  }

  get formattedAmountToFreeShipping(): string {
    if (!this.freeShippingCoupon) {
      return '';
    }
    const freeShippingThreshold = Number(this.freeShippingCoupon.orderMinCost) || 0;
    return this.formatPrice(Math.max(freeShippingThreshold - this.cartTotal, 0));
  }

  get formattedAmountToFreeGift(): string {
    if (!this.freeGiftCoupon) {
      return '';
    }
    let freeGiftThreshold = Number(this.freeGiftCoupon.orderMinCost);
    if (isNaN(freeGiftThreshold)) {
      freeGiftThreshold = 0;
    }
    return this.formatPrice(Math.max(freeGiftThreshold - this.cartTotal, 0));
  }

  get formattedCartTotal(): string {
    return this.formatPrice(this.cartTotal);
  }

  get formattedFreeShippingThreshold(): string {
    if (!this.freeShippingCoupon) {
      return '';
    }
    let freeShippingThreshold = Number(this.freeShippingCoupon.orderMinCost);
    if (isNaN(freeShippingThreshold)) {
      freeShippingThreshold = 0;
    }
    return this.formatPrice(freeShippingThreshold);
  }

  get formattedFreeGiftThreshold(): string {
    if (!this.freeGiftCoupon) {
      return '';
    }
    return this.formatPrice(Number(this.freeGiftCoupon.orderMinCost) ?? 0);
  }

  get progressPercent(): number {
    const freeGiftThreshold = (this.isFreeGiftAvailable && Number(this.freeGiftCoupon?.orderMinCost)) || 0;
    const freeShippingThreshold = (this.isFreeShippingAvailable && Number(this.freeShippingCoupon?.orderMinCost)) || 0;
    const max = Math.max(freeGiftThreshold, freeShippingThreshold);
    if (this.cartTotal >= max) {
      return 100;
    }
    if (this.cartTotal < freeShippingThreshold && freeGiftThreshold) {
      return Math.max(0, ((this.cartTotal / freeShippingThreshold) * 100) / 2);
    }
    const percents = (this.cartTotal / max) * 100;
    return Math.max(0, Math.min(100, parseInt(percents.toString(), 10)));
  }

  get cartTotalMarkerPosition(): string {
    const percent = this.progressPercent;
    if (percent === 0 || percent >= 100) {
      return `${percent}%`;
    }

    const hasMidThreshold = this.isFreeShippingAvailable && this.isFreeGiftAvailable;

    if (hasMidThreshold) {
      if (percent < 50) {
        return `min(${percent}%, calc(50% - ${MARKER_WIDTH}))`;
      }
      return `max(calc(50% + ${MARKER_WIDTH}), min(${percent}%, calc(100% - ${MARKER_WIDTH})))`;
    }
    return `min(${percent}%, calc(100% - ${MARKER_WIDTH}))`;
  }

  get ordersCount(): number {
    return this.shoppingCartStore.getSubjects().length;
  }

  get hasPackagesInAllSubjects(): boolean {
    return this.shoppingCartStore.hasPackagesInAllSubjects();
  }

  get hasAnyEmptyOrderInCart(): boolean {
    const subjects: ISubject[] = this.shoppingCartStore.getSubjects();
    return subjects?.some((_, index) => this.shoppingCartStore.getNumberOfItemsInOrder(index) === 0);
  }

  get amountString(): string {
    return this.ordersCount > 0 ? `x${this.ordersCount} = ` : '';
  }

  get oneItemPrice(): number {
    const discountPrice: number = this.currentProduct?.discountedPriceWithPackage;
    const unitPrice: number = this.currentProduct?.unitPrice;
    return this.hasPackagesInAllSubjects ? discountPrice : unitPrice;
  }

  get finalPrice(): string {
    const amountOrders: number = this.ordersCount;
    const currentProduct: number = this.oneItemPrice;

    return (amountOrders * currentProduct).toFixed(2);
  }

  get currentProduct(): RewardUpsellProduct {
    return this.freeGiftProduct ?? this.upsellProducts[this.currentProductIndex];
  }

  get currentProductImage() {
    const item = this.currentProduct;
    if (!item) {
      return null;
    }

    return this.contentStore.getProductImage(item.skuCode, item.defaultLayoutThemeId) ?? null;
  }

  $onInit() {
    this.cartRewardTrackerService.setEnabled(this.enabled);
    this.cartRewardTrackerService.setEnabledFreeShipping(this.isFreeShippingAvailable);
    this.cartRewardTrackerService.setCouponPrice(
      Number(this.freeShippingCoupon?.orderMinCost) ?? NaN,
      Number(this.freeGiftCoupon?.orderMinCost) ?? NaN
    );
    this.initializeFreeGiftState();
  }

  private initializeFreeGiftState(): void {
    if (!this.shoppingCartStore.hasFreeGift()) {
      this.resetFreeGiftState();
      return;
    }

    const freeGiftAddon = this.shoppingCartStore.getFreeGiftAddOn();

    this.freeGiftProduct = {
      ...freeGiftAddon?.orderable,
      showSuccess: true,
      successMessageKey: 'IN_YOUR_CART',
      freeGift: true
    };
  }

  public updateUpsellProducts(): void {
    if (this.shoppingCartStore.hasFreeGift() && this.freeGiftProduct) {
      this.upsellProducts = [this.freeGiftProduct];
      return;
    }
    if (this.hasAnyEmptyOrderInCart) {
      this.upsellProducts = [];
      return;
    }
    if (!this.shoppingCartStore.hasFreeGift()) {
      this.resetFreeGiftState();
    }
    const currentProduct = this.currentProduct;
    const upsellProductsToBeAdded = (this.upsellProducts || []).filter((p) => p?.showSuccess && !p.freeGift);
    const someInProgress = upsellProductsToBeAdded.length > 0;
    //merge new addons with "in progress" to continue showing added and add new ones to keep them working. reverse is for quality of live, so that on next it show not added(not each time, but works better then without)
    const newAddOns = (this.jobStore?.getSuggestedAddOns() || [])
      .filter((addon) => addon && !addon.freeGift)
      .filter((addon) => !upsellProductsToBeAdded.some((p) => p.skuCode === addon.skuCode));

    upsellProductsToBeAdded.push(...newAddOns);
    this.upsellProducts = upsellProductsToBeAdded;
    //not to lose the position
    if (someInProgress) {
      this.currentProductIndex = this.upsellProducts.findIndex((product) => product.skuCode === currentProduct.skuCode);
    }
  }

  public nextProduct(): void {
    this.currentProductIndex = (this.currentProductIndex + 1) % this.upsellProducts.length;
  }

  private formatPrice(price: number) {
    return `$${parseInt(price.toString(), 10)}`;
  }

  public prevProduct(): void {
    this.currentProductIndex = (this.currentProductIndex - 1 + this.upsellProducts.length) % this.upsellProducts.length;
  }

  public addCurrentToCart(): void {
    const product = this.currentProduct;
    if (!this.onAddToCart) return;

    this.suppressConfetti = true;

    this.onAddToCart({ product }).catch((err) => console.error('Add to cart failed', err));

    this.showSuccessForProduct(product);
  }

  private showSuccessForProduct(upsellProduct: RewardUpsellProduct): void {
    upsellProduct.showSuccess = true;
    upsellProduct.successMessageKey = 'ADDED_TO_CART';
    if (this.successTimeoutRef) {
      this.$timeout.cancel(this.successTimeoutRef);
    }
    if (this.currentProduct.freeGift) {
      return;
    }
    this.successTimeoutRef = this.$timeout(
      () => {
        if (this.currentProduct.showSuccess) {
          this.nextProduct();
        }
        this.upsellProducts.forEach((product) => {
          product.showSuccess = false;
          product.successMessageKey = null;
        });
      },
      SUCCESS_DURATION,
      true
    );
  }

  private showSuccessForFreeGift(freeGiftProduct: RewardUpsellProduct): void {
    this.freeGiftProduct = {
      ...freeGiftProduct,
      showSuccess: true,
      successMessageKey: 'ADDED_TO_CART',
      freeGift: true
    };
    if (this.successTimeoutRef) {
      this.$timeout.cancel(this.successTimeoutRef);
    }
    this.successTimeoutRef = this.$timeout(() => {
      if (this.freeGiftProduct) {
        this.freeGiftProduct.showSuccess = true;
        this.freeGiftProduct.successMessageKey = 'IN_YOUR_CART';
      }
    }, SUCCESS_DURATION);
  }

  private resolveState(): State {
    const subtotal: number = this.cartTotal;
    const giftThreshold: number = Number(this.freeGiftCoupon?.orderMinCost) || 0;
    const shippingThreshold: number = Number(this.freeShippingCoupon?.orderMinCost) || 0;
    const giftExists: boolean = this.isFreeGiftAvailable;
    const shippingExists: boolean = this.isFreeShippingAvailable;

    // should be disabled, so safe exist
    if (!giftExists && !shippingExists) return State.NA;

    // Empty cart
    if (subtotal === 0) {
      return shippingExists ? State.EMPTY_TO_FREE_SHIPPING : State.EMPTY_TO_FREE_GIFT;
    }

    // Shipping-only flow
    if (!giftExists && shippingExists) {
      return subtotal < shippingThreshold ? State.NONE_TO_FREE_SHIPPING : State.FREE_SHIPPING_END;
    }

    // Gift-only flow
    if (giftExists && !shippingExists) {
      return subtotal < giftThreshold ? State.NONE_TO_FREE_GIFT : State.FREE_GIFT;
    }

    if (subtotal < shippingThreshold) {
      return State.NONE_TO_FREE_SHIPPING;
    }
    if (subtotal < giftThreshold) {
      return State.FREE_SHIPPING_TO_FREE_GIFT;
    }
    return State.FREE_GIFT;
  }
}

RewardTrackerController.$inject = [
  'ShoppingCartStore',
  'ContentStore',
  'JobStore',
  'CartRewardTrackerService',
  'ConfettiService',
  '$scope',
  'SessionStorageService',
  'ProductCatalogStore',
  '$timeout',
  '$rootScope'
];
App.component('ltCartRewardTracker', {
  template,
  bindings: {
    onAddToCart: '&?'
  },
  transclude: true,
  controller: RewardTrackerController,
  controllerAs: '$ctrl'
});
